import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { fetchPosts } from "@/content/blog";
import Seo, { websiteLd, getSiteUrl, breadcrumbLd } from "@/components/Seo";
import { ArrowRight, Clock, CalendarDays } from "lucide-react";

const SITE_URL = getSiteUrl();

function formatDate(iso, lang) {
  try {
    return new Date(iso).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function BlogIndexPage({ forceLang }) {
  const { lang, setLang, t } = useLang();
  useEffect(() => {
    if (forceLang && forceLang !== lang) setLang(forceLang);
  }, [forceLang, lang, setLang]);
  const effectiveLang = forceLang || lang;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPosts(effectiveLang).then((data) => {
      if (!alive) return;
      setPosts(data);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [effectiveLang]);

  const basePath = effectiveLang === "es" ? "/blog" : "/journal";
  const title = `${t("blog.title")} | Cortijo Santa María`;
  const description = t("blog.subtitle");
  const canonical = `${SITE_URL}${basePath}`;
  const alternates = {
    es: `${SITE_URL}/blog`,
    en: `${SITE_URL}/journal`,
    "x-default": `${SITE_URL}/blog`,
  };

  return (
    <main data-testid="blog-index" className="min-h-screen bg-cream-100 text-charcoal">
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        alternates={alternates}
        lang={effectiveLang}
        ogType="website"
        jsonLd={[
          websiteLd(),
          breadcrumbLd([
            { name: t("nav_ecom.home"), url: `${SITE_URL}/` },
            { name: t("blog.title"), url: canonical },
          ]),
        ]}
      />
      <ShopNav />

      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-20 sm:pt-28 pb-12">
        <p className="text-[11px] uppercase tracking-[0.32em] text-gold-deep mb-5">
          {t("blog.overline")}
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-olive-900 max-w-3xl">
          {t("blog.title")}
        </h1>
        <p className="mt-6 text-olive-800/80 text-base sm:text-lg leading-relaxed font-light max-w-2xl">
          {t("blog.subtitle")}
        </p>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 lg:px-12 pb-28">
        {loading ? (
          <div data-testid="blog-loading" className="text-olive-800/60 font-light">…</div>
        ) : posts.length === 0 ? (
          <div data-testid="blog-empty" className="text-olive-800/60 font-light">
            {effectiveLang === "es" ? "Aún no hay publicaciones." : "No posts yet."}
          </div>
        ) : (
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {posts.map((p) => (
              <li key={p.slug}>
                <Link
                  to={`${basePath}/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group block h-full border border-cream-400 bg-cream-50 hover:border-olive-700 transition-colors"
                >
                  <div className="aspect-[4/3] bg-cream-200 overflow-hidden">
                    <img
                      src={p.hero_image}
                      alt={p.title}
                      loading="lazy"
                      className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 lg:p-7 flex flex-col">
                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] text-olive-800/60 mb-4">
                      <span>{p.category}</span>
                      {p.read_time ? (
                        <>
                          <span aria-hidden="true">·</span>
                          <span className="inline-flex items-center gap-1.5">
                            <Clock size={11} strokeWidth={1.3} />
                            {p.read_time}
                          </span>
                        </>
                      ) : null}
                    </div>
                    <h2 className="font-serif text-xl lg:text-2xl text-olive-900 leading-snug group-hover:text-gold-deep transition-colors">
                      {p.title}
                    </h2>
                    <p className="mt-3 text-sm text-olive-800/75 leading-relaxed font-light">
                      {p.excerpt}
                    </p>
                    <div className="mt-6 flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-olive-800/60">
                        <CalendarDays size={12} strokeWidth={1.3} />
                        {formatDate(p.date, effectiveLang)}
                      </span>
                      <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-gold-deep">
                        {t("blog.read_more")}
                        <ArrowRight size={13} strokeWidth={1.3} />
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Footer />
    </main>
  );
}
